
/**
 * Shared visual frame for all auth pages: gradient background tuned to the
 * Squishy logo palette (navy → teal), brand mark, centered card.
 */
export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="h-screen flex flex-col overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at 25% 0%, #14b8a6 0%, #0d9488 30%, #1e1b4b 80%)',
      }}
    >
      <main className="flex-1 flex flex-col items-center justify-center px-4">
        <img
          src="/logo-full.png"
          alt="Squishy School"
          className="w-44 sm:w-52 mb-2 drop-shadow-xl"
          style={{ filter: 'drop-shadow(0 12px 25px rgba(0,0,0,0.35))' }}
        />
        {children}
      </main>
    </div>
  );
}

export function AuthCard({
  title, subtitle, children,
}: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-7 mt-2">
      <h1 className="text-2xl font-bold text-squishy-navy">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

export function AuthInput(props: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        autoFocus={props.autoFocus}
        required={props.required}
        placeholder={props.placeholder}
        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-squishy-teal focus:border-squishy-teal transition"
      />
      {props.error && <span className="text-xs text-red-600 mt-1 block">{props.error}</span>}
    </label>
  );
}

export function AuthButton({ children, loading, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={loading || rest.disabled}
      className="w-full bg-squishy-tealDark text-white font-semibold py-3 rounded-lg hover:bg-squishy-navy active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition shadow-soft"
    >
      {loading ? '…' : children}
    </button>
  );
}

export function AuthError({ message }: { message: string }) {
  return (
    <div role="alert" className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
      {message}
    </div>
  );
}

export function AuthSuccess({ message }: { message: string }) {
  return (
    <div role="status" className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2">
      {message}
    </div>
  );
}
